/**
 * Versioned UK constants for the indicative solar savings calculation.
 * Named with their vintage where rates drift over time so a future refresh
 * is an explicit change, not a silent re-meaning.
 */

/** Fraction of generation consumed on-site without a battery. */
export const SELF_CONSUMPTION_FRACTION = 0.35;

/** Fraction of generation consumed on-site with a battery. */
export const SELF_CONSUMPTION_FRACTION_WITH_BATTERY = 0.65;

/**
 * The savings regions the indicative calculation has real rates for.
 * Accounts in other regions fall back to 'uk' semantics via
 * {@link solarSavingsRegion} — see that function before adding a region.
 */
export type SolarSavingsRegion = 'uk' | 'us';

/**
 * Per-region tariff + cost assumptions for the indicative savings/payback.
 * All money is expressed as MINOR units per kWh (pence / cents) and MAJOR
 * units per panel, in the region's currency.
 *
 * Shared by the provider designer and estimate documents.
 *
 * - uk (2026 price-cap era): 27p/kWh retail, 15p/kWh Smart Export
 *   Guarantee, ~£400/panel installed.
 * - us (2026 national averages): 17¢/kWh retail; 7¢/kWh export (post-NEM-3
 *   net-billing style — varies hugely by state); ~$1,000/panel installed
 *   (~$2.5/W at 400 W). Google models US economics per monthly-bill bracket
 *   too, but for a system it sized to that bill rather than the one being
 *   designed, so none of these figures come from it.
 */
export const SOLAR_SAVINGS_RATES: Record<
  SolarSavingsRegion,
  {
    currencySymbol: string;
    retailTariffMinorPerKwh: number;
    exportTariffMinorPerKwh: number;
    indicativeCostPerPanelMajor: number;
  }
> = {
  uk: {
    currencySymbol: '£',
    retailTariffMinorPerKwh: 27.0,
    exportTariffMinorPerKwh: 15.0,
    indicativeCostPerPanelMajor: 400,
  },
  us: {
    currencySymbol: '$',
    retailTariffMinorPerKwh: 17.0,
    exportTariffMinorPerKwh: 7.0,
    indicativeCostPerPanelMajor: 1000,
  },
};

/**
 * Years the UK savings series runs over. Matches the 20-year design life
 * Google reports as `panelLifetimeYears` on every roof we have analysed.
 */
export const SOLAR_SAVINGS_HORIZON_YEARS = 20;

/**
 * Years the US savings series runs over. Shaw and typical US residential
 * warranties quote 25 years, not Google's 20-year panelLifetimeYears.
 */
export const US_SOLAR_SAVINGS_HORIZON_YEARS = 25;

export const solarSavingsHorizonYears = (region: SolarSavingsRegion): number =>
  region === 'us'
    ? US_SOLAR_SAVINGS_HORIZON_YEARS
    : SOLAR_SAVINGS_HORIZON_YEARS;

/**
 * Annual panel output loss, percent. 0.5% is the NREL SAM default for
 * crystalline silicon and the rate most module warranties underwrite. It
 * applies in both regions: a series that grows the tariff for twenty years
 * but never ages the panels only ever errs upwards.
 *
 * The MCS performance estimate is untouched — MIS 3002 4.1 is a FIRST-YEAR
 * figure, and degradation only shapes years two onward of the savings series.
 */
export const DEFAULT_PANEL_DEGRADATION_PERCENT = 0.5;

export const solarSavingsDegradationPercent = (
  _region: SolarSavingsRegion
): number => DEFAULT_PANEL_DEGRADATION_PERCENT;

/**
 * Annual growth in the electricity unit rate, percent, used to age the
 * year-one savings across {@link SOLAR_SAVINGS_HORIZON_YEARS}.
 *
 * 3% approximates long-run UK CPI and the twenty-year domestic electricity
 * price trend, excluding the 2022 spike as an outlier. Both the rate and
 * the assumption print on the document.
 */
export const DEFAULT_ELECTRICITY_PRICE_GROWTH_PERCENT = 3;

/**
 * Map an account's region code onto a savings region: 'us' gets US rates,
 * everything else (uk/au/za/unknown) falls back to 'uk' — wrong for au/za,
 * but those regions have no solar rollout yet and the UK fallback keeps the
 * behaviour explicit rather than accidental.
 */
export const solarSavingsRegion = (
  accountRegion: string | null | undefined
): SolarSavingsRegion => (accountRegion?.toLowerCase() === 'us' ? 'us' : 'uk');

/**
 * Optional per-account retail / export rates. Null or omitted fields fall
 * back to {@link SOLAR_SAVINGS_RATES} for the region. Zero is a real rate
 * (a free-export tariff) and must not be treated as "use the default".
 */
export type SolarTariffOverrides = {
  retailTariffMinorPerKwh?: number | null;
  exportTariffMinorPerKwh?: number | null;
};

export const resolveSolarSavingsRates = (
  region: SolarSavingsRegion,
  overrides?: SolarTariffOverrides | null
): (typeof SOLAR_SAVINGS_RATES)[SolarSavingsRegion] => {
  const defaults = SOLAR_SAVINGS_RATES[region];
  const retail = overrides?.retailTariffMinorPerKwh;
  const exported = overrides?.exportTariffMinorPerKwh;
  return {
    ...defaults,
    retailTariffMinorPerKwh:
      retail != null && Number.isFinite(retail)
        ? retail
        : defaults.retailTariffMinorPerKwh,
    exportTariffMinorPerKwh:
      exported != null && Number.isFinite(exported)
        ? exported
        : defaults.exportTariffMinorPerKwh,
  };
};
